import * as THREE from 'three';
import React from 'react';
import EffectorBase, { DurationDeterminingEffector, EffectorPropertyDrawer, ReversibleEffector } from "./EffectorBase";
import Engine from '../../Engine';
import { PropertyTypes, CustomEventTypes } from "../../../globals";
import DropdownProperty from "../../../components/properties/DropdownProperty";
import { TimelineFragment } from '../TimelineFragment';
import { IDropdownOption } from '../../../interfaces/Misc';


interface EffectorObjectAnimationSaveData
{
	clipIndex: number;
	reversed: boolean;
	clipName: string;
}

export default class EffectorObjectAnimation extends EffectorBase<EffectorObjectAnimation> implements DurationDeterminingEffector, ReversibleEffector
{
	clipDurations: number[];
	clipIndex: number;
	clipName: string;
	reversed: boolean;
	
	constructor(fragment : TimelineFragment)
	{
		super(fragment);
		this.clipDurations = [];
		this.clipIndex = 0;
		this.clipName = "";
		this.reversed = false;
		this.setClipToPlay(this.clipIndex);
	}

	getInitialDuration = () => 
	{
		if (this.clipDurations.length > 0)
		{
			return this.clipDurations[this.clipIndex];
		}
		return 0;
	}

	save = () : EffectorObjectAnimationSaveData =>
	{
		const output = {
			clipIndex: this.clipIndex,
			reversed: this.reversed,
			clipName: this.clipName,
		};

		return output;
	};

	load = (savedData: EffectorObjectAnimationSaveData) =>
	{
		this.clipIndex = savedData.clipIndex;
		this.reversed = savedData.reversed;
		this.clipName = savedData.clipName;
		this.setClipToPlay(this.clipIndex);
	};
		
	copyValues = (sourceEffector : EffectorObjectAnimation) =>
	{
		this.setClipToPlay(sourceEffector.clipIndex);
		this.reversed = sourceEffector.reversed;
	}

	onAdded = () =>
	{
		this.clipIndex = 0;
		const targetObject = this.targetObjects()[0];
		if (targetObject && targetObject.userData.animations)
		{
			this.clipName = targetObject.userData.animations[0].name;
		}
		this.setClipToPlay(this.clipIndex);
	}

	setClipToPlay = (clipId: number) =>
	{
		this.clipIndex = clipId ? clipId : 0;
		this.clipDurations.length = 0;

		const targetObject = this.targetObjects()[0];
		if (targetObject && targetObject.userData.animations)
		{
			this.clipName = targetObject.userData.animations[this.clipIndex].name;
		}

		this.forEachTargetObject((targetObject) =>
		{
			const mixer = Engine.getInstance().getAnimationMixer(targetObject.userData.guid);
			if (mixer)
			{
				mixer.clipAction(targetObject.userData.animations[this.clipIndex]).play();
				this.clipDurations.push(targetObject.userData.animations[this.clipIndex].duration);
			}
		});
	}

	resetState = () =>
	{

	}

	update = (fragmentTime : number) =>
	{
		this.forEachTargetObject((targetObject) =>
		{
			const mixer = Engine.getInstance().getAnimationMixer(targetObject.userData.guid);
			if (mixer)
			{
				mixer.setTime(fragmentTime * this.fragment.getDuration());
				if (Engine.getInstance().getPickingManager().isObjectSelected(targetObject))
				{
					document.dispatchEvent(new CustomEvent(CustomEventTypes.selectedObjectAnimated));
				}
			}
		});
	}

	generateClipList = () =>
	{
		const clips: IDropdownOption[] = [];
		const targetObject = this.targetObjects()[0];
		if (targetObject && targetObject.userData.animations)
		{
			targetObject.userData.animations.forEach((anim : THREE.AnimationClip, idx: number) =>
			{
				clips.push({ display: anim.name, value: idx });
			});
		}

		return clips;
	}

	getEditorProperties = () =>
	{
		const targetObject = this.targetObjects()[0];
		const ret = [...this.getCommonPropertyDrawers()];
		if (targetObject && targetObject.userData.animations)
		{
			ret.push(new EffectorPropertyDrawer(
				"Clip to Play",
				PropertyTypes.DropDown,
				<DropdownProperty
					value={this.clipIndex}
					options={this.generateClipList()}
				/>,
				() => {return this.clipIndex;},
				(v) => {this.setClipToPlay(v);},
			));
		}
		return ret;
	}
}
